import { useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useCurrency } from "@/context/CurrencyContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const METHODS = [
  { value: "cash", label: "Cash" },
  { value: "mtn_money", label: "MTN Mobile Money" },
  { value: "airtel_money", label: "Airtel Money" },
  { value: "bank_transfer", label: "Bank Transfer" },
];

export default function PaymentDialog({ open, onOpenChange, invoice, onSaved }) {
  const [method, setMethod] = useState("cash");
  const [amount, setAmount] = useState("");
  const [reference, setReference] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const { currency, rates, fmt } = useCurrency();

  const balanceZmw = invoice ? invoice.total - invoice.amount_paid : 0;
  const balanceDisplay = currency === "ZMW" ? balanceZmw : balanceZmw * (rates[currency] || 1);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post(`/invoices/${invoice.id}/payments`, {
        method, amount: parseFloat(amount), currency, reference, note,
      });
      toast.success("Payment recorded");
      setAmount(""); setReference(""); setNote("");
      onOpenChange(false);
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="payment-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl">Record a payment</DialogTitle>
        </DialogHeader>
        <div className="border border-border bg-secondary/50 px-4 py-3 text-sm" data-testid="payment-balance">
          Balance due: <span className="font-heading text-lg font-bold">{fmt(balanceZmw)}</span>
          {currency !== "ZMW" && <span className="ml-2 text-xs text-muted-foreground">(K {balanceZmw.toFixed(2)} ZMW)</span>}
        </div>
        <form onSubmit={submit} className="mt-2 space-y-4">
          <div className="space-y-1.5">
            <Label>Payment method</Label>
            <Select value={method} onValueChange={setMethod}>
              <SelectTrigger data-testid="payment-method"><SelectValue /></SelectTrigger>
              <SelectContent>
                {METHODS.map((m) => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Amount ({currency})</Label>
            <Input type="number" min="0.01" step="0.01" data-testid="payment-amount" value={amount}
              onChange={(e) => setAmount(e.target.value)} placeholder={balanceDisplay.toFixed(2)} required />
          </div>
          <div className="space-y-1.5">
            <Label>Reference / receipt no. (optional)</Label>
            <Input data-testid="payment-reference" value={reference} onChange={(e) => setReference(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label>Note (optional)</Label>
            <Input data-testid="payment-note" value={note} onChange={(e) => setNote(e.target.value)} />
          </div>
          <Button type="submit" disabled={saving} data-testid="payment-submit" className="w-full transition-colors">
            {saving ? "Recording…" : "Record payment"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
